'use client'

import * as React from 'react'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { Button } from '@repo/design-system-cf/components/button'
import { IconArrowOutward } from '@repo/design-system-cf/components/icons'
import { domAnimation, LazyMotion, m, useInView } from 'framer-motion'

interface ToolFeatureCardProps {
  title: string
  description: string
  buttonText: string
  imageSrc: string
  href: string
  icon?: React.ReactNode
  priority?: boolean
  className?: string
  delay?: number
}

export function ToolFeatureCard({
  title,
  description,
  buttonText,
  imageSrc,
  href,
  icon,
  priority,
  className = '',
  delay = 0,
}: ToolFeatureCardProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { amount: 0.5 })

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className={`@container h-full ${className}`}>
        <div className="relative flex h-full flex-col overflow-hidden rounded-lg border border-[#D1D5DB]/40 bg-[#F9FAFB] min-h-[220px] @[350px]:flex-row">
          <div className="flex flex-col justify-center gap-3 p-6">
            <h3 className="font-poppins text-lg lg:text-xl font-semibold text-[#1A1C1F]">
              {icon && <>{icon}</>}
              {title}
            </h3>
            <p className="text-sm text-[#1A1C1F]/70">{description}</p>
            <Button variant="primary" size="small" asChild className="self-start">
              <Link href={href} target="_blank" rel="noopener noreferrer">
                {buttonText}
                <IconArrowOutward />
              </Link>
            </Button>
          </div>
          <div className="relative flex shrink-0 items-end justify-end overflow-visible w-full @[350px]:w-2/5">
            <m.div
              className="[transform-origin:10%_60%]"
              animate={{
                rotate: isInView ? -4 : 0,
                y: isInView ? 0 : 60,
              }}
              transition={{ type: 'spring', bounce: 0.4, duration: 0.8, delay }}
            >
              <Image
                src={imageSrc}
                alt=""
                width={400}
                height={250}
                sizes="(min-width: 768px) 15vw, 40vw"
                priority={priority}
                className="-mb-24 @[350px]:-mb-1 -mr-3 @[350px]:mr-0 rounded-lg object-contain shadow-lg translate-x-[10px]"
              />
            </m.div>
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}
